export const defaultColumnWidth = 384;
export function cloneComponentConfigs(configs) {
    const cloned = [];
    configs.forEach((c) => {
        cloned.push(Object.assign({}, c));
    });
    return cloned;
}
export function getDashboardLayoutHeight(layoutHeight, rowHeight, gutter) {
    if (layoutHeight === 0)
        return 0;
    return layoutHeight * rowHeight + (layoutHeight - 1) * gutter;
}
export function getDashboardLayoutWidth(columnCount, columnWidth, gutter) {
    const width = columnWidth || defaultColumnWidth;
    if (columnCount === 0)
        return width;
    const totalColumnWidth = width * columnCount;
    const totalGutterWidth = (columnCount - 1) * gutter;
    return totalColumnWidth + totalGutterWidth;
}
export function isOutOfRange(columnCount, top, left, width) {
    if (top < 0)
        return true;
    if (left < 0)
        return true;
    if (left + width > columnCount)
        return true;
    return false;
}
export function getCollidingElementIds(layout, columnCount, componentConfig) {
    const ids = {};
    while (layout.length < componentConfig.top + componentConfig.height) {
        layout.push(new Array(columnCount).fill(''));
    }
    for (let i = componentConfig.top; i < componentConfig.top + componentConfig.height; i++) {
        for (let j = componentConfig.left; j < componentConfig.left + componentConfig.width; j++) {
            if (layout[i][j] !== '' && layout[i][j] !== componentConfig.id) {
                ids[layout[i][j]] = true;
            }
        }
    }
    return Object.keys(ids);
}
export function findAvailableLocation(layout, columnCount, componentConfig) {
    for (let i = 0; i < layout.length; i++) {
        for (let j = 0; j < columnCount; j++) {
            if (!isOutOfRange(columnCount, i, j, componentConfig.width) &&
                getCollidingElementIds(layout, columnCount, Object.assign(Object.assign({}, componentConfig), { top: i, left: j })).length === 0) {
                return {
                    top: i,
                    left: j,
                };
            }
        }
    }
    return {
        top: layout.length,
        left: 0,
    };
}
export function fillLayoutWithComponent(layout, columnCount, component) {
    while (layout.length < component.top + component.height) {
        layout.push(new Array(columnCount).fill(''));
    }
    for (let i = component.top; i < component.top + component.height; i++) {
        for (let j = component.left; j < component.left + component.width; j++) {
            layout[i][j] = component.id;
        }
    }
}
export function getMaxColumnCount(columnWidth, initialColumnCount, gutter, dashboardWidth) {
    let columnCount = initialColumnCount;
    for (; columnCount > 0; columnCount--) {
        const width = getDashboardLayoutWidth(columnCount, columnWidth, gutter);
        if (width <= dashboardWidth)
            return columnCount;
    }
    return 1;
}
export function componentLocationComparator(a, b) {
    if (a.top > b.top) {
        return 1;
    }
    else if (a.top < b.top) {
        return -1;
    }
    if (a.left > b.left) {
        return 1;
    }
    else if (a.left < b.left) {
        return -1;
    }
    else {
        return 0;
    }
}
export function generateFluidDashboardLayout(dashboard, dashboardWidth) {
    const { config, components } = dashboard;
    config.columnCount = getMaxColumnCount(config.columnWidth, config.columnCount, config.gutter, dashboardWidth);
    components.sort(componentLocationComparator);
    const layout = [];
    const nextComponents = [];
    const maxWidth = config.columnCount;
    components.forEach((component) => {
        const nextComponent = Object.assign(Object.assign({}, component), { width: component.width > maxWidth ? maxWidth : component.width });
        const newLocation = findAvailableLocation(layout, config.columnCount, nextComponent);
        nextComponent.top = newLocation.top;
        nextComponent.left = newLocation.left;
        fillLayoutWithComponent(layout, config.columnCount, nextComponent);
        nextComponents.push(nextComponent);
        return Object.assign({}, component);
    });
    return {
        layout,
        components: nextComponents,
        config,
        width: getDashboardLayoutWidth(config.columnCount, config.columnWidth, config.gutter),
        height: getDashboardLayoutHeight(layout.length, config.rowHeight, config.gutter),
    };
}
export function generateFixedDashboardLayout(dashboard, dashboardWidth) {
    const layout = [];
    const { config, components } = dashboard;
    if (components.length === 0) {
        layout.push(new Array(config.columnCount).fill(''));
    }
    components.map((componentConfig) => {
        if (componentConfig.top === -1 || componentConfig.left === -1)
            return;
        while (layout.length < componentConfig.top + componentConfig.height) {
            layout.push(new Array(config.columnCount).fill(''));
        }
        for (let i = componentConfig.top; i < componentConfig.top + componentConfig.height; i++) {
            for (let j = componentConfig.left; j < componentConfig.left + componentConfig.width; j++) {
                layout[i][j] = componentConfig.id;
            }
        }
    });
    return {
        layout,
        components,
        width: dashboardWidth,
        config,
        height: getDashboardLayoutHeight(layout.length, config.rowHeight, config.gutter),
    };
}
export function generateDashboardLayout(dashboard, dashboardWidth) {
    const cloned = JSON.parse(JSON.stringify(dashboard));
    const { config } = cloned;
    const width = getDashboardLayoutWidth(config.columnCount, config.columnWidth, config.gutter);
    const isLayoutLargerThanDashboard = width > Math.ceil(dashboardWidth);
    return isLayoutLargerThanDashboard
        ? generateFluidDashboardLayout(cloned, dashboardWidth)
        : generateFixedDashboardLayout(cloned, dashboardWidth);
}
export function debounce(func, wait) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let h;
    return () => {
        clearTimeout(h);
        h = setTimeout(() => func(), wait);
    };
}
export function getDashboardComponentTop(top, rowHeight, gutter) {
    if (top === 0)
        return 0;
    let result = 0;
    result += top * rowHeight;
    result += top * gutter;
    return result;
}
export function getDashboardComponentHeight(height, rowHeight, gutter) {
    if (height === 0)
        return 0;
    let result = 0;
    result += height * rowHeight;
    result += (height - 1) * gutter;
    return result;
}
export function getDashboardComponentLeft(left, columnCount, gutter) {
    if (left === 0)
        return 0;
    const totalGutterWidth = (columnCount - 1) * gutter;
    return `calc(((100% - ${totalGutterWidth}px) / ${columnCount}) * ${left} + ${gutter * left}px)`;
}
export function getDashboardComponentWidth(width, columnCount, gutter) {
    if (width === 0)
        return 0;
    const totalGutterWidth = (columnCount - 1) * gutter;
    const includedGutterWidth = (width - 1) * gutter;
    return `calc(((100% - ${totalGutterWidth}px) / ${columnCount}) * ${width} + ${includedGutterWidth}px)`;
}
//# sourceMappingURL=Dashboard.utils.js.map